<template>
  <nav :class="b({ highlightFeatures })">
    <ul :class="b('list')">
      <li
        v-for="item in mappedUserNavigation"
        :key="item.linkHref"
        :class="b('item')"
      >
        <a
          :class="b('link')"
          :href="item.linkHref"
          :target="item.linkTarget"
          :title="item.linkTitle"
        >
          <span
            v-if="item.highlight"
            :class="b('link-highlight-badge')"
          ></span>
          <e-icon-sprite
            :class="b('icon')"
            :alt="$t('c-user-navigation.logoutButtonText')"
            :icon="item.icon"
            size="24"
          />
          {{ item.linkText }}
        </a>
      </li>
      <template v-if="!sessionStore.disableCdc">
        <li
          v-if="sessionStore.getIsLoggedIn && !sessionStore.getUserIsInternal"
          :class="b('item')"
        >
          <button
            :class="b('link')"
            type="button"
            @click="$cdc.auth.logout()"
          >
            <e-icon-sprite
              :class="b('icon')"
              :alt="$t('c-user-navigation.logoutButtonText')"
              icon="i-user-logout"
              size="24"
            />
            {{ $t('c-user-navigation.logoutButtonText') }}
          </button>
        </li>
        <li
          v-else-if="!sessionStore.getIsLoggedIn && sessionStore.isLoginEnabled"
          :class="b('item')"
        >
          <button
            :class="b('link')"
            type="button"
            @click="$cdc.auth.openLogin()"
          >
            <e-icon-sprite
              :class="b('icon')"
              :alt="$t('c-user-navigation.loginButtonText')"
              icon="i-user-login"
              size="24"
            />
            {{ $t('c-user-navigation.loginButtonText') }}
          </button>
        </li>
      </template>
    </ul>
  </nav>
</template>

<script>
  import { COOKIE_NAME_MAP } from '@/setup/globals';
  import useSessionStore from '@/stores/session';
  import eIconSprite from '@/elements/e-icon-sprite.vue';

  /**
   * @type {Number} The amount of milliseconds features have to be highlighted.
   */
  const HIGHLIGHT_FEATURE_TIME = 5000;

  /**
   * Renders the user navigation.
   */
  export default {
    name: 'c-user-navigation',
    components: {
      eIconSprite,
    },

    // mixins: [],

    props: {
      /**
       * Flag for recognizing if the component is visible for the user.
       * Needed for mobile where that is not always the case.
       */
      visible: {
        type: Boolean,
        default: true,
      },
    },
    setup() {
      return {
        sessionStore: useSessionStore(),
      };
    },
    data() {
      return {
        /**
         * @type {Boolean} - Flag for recognizing if the component was mounted.
         */
        isMounted: false,

        /**
         * @type {Boolean} - Flag for disabling feature highlighting after a defined time.
         */
        highlightFeatures: true,
      };
    },

    computed: {
      /**
       * Checks if the component was shown to the user.
       *
       * @returns {Boolean}
       */
      isReady() {
        return this.isMounted && this.visible;
      },

      /**
       * Gets the user navigation.
       *
       * @returns {Array.<Object>|null}
       */
      mappedUserNavigation() {
        const { userNavigation } = this.sessionStore;

        if (!Array.isArray(userNavigation)) {
          return null;
        }

        return userNavigation.map((item) => ({
          ...item,
          highlight: item.linkHighlighted && !this.isHighlightCookieSet(item.crossSystemId),
        }));
      },
    },
    watch: {
      /**
       * Checks if the component was shown to the user for disabling
       * the highlight badges after the defined time.
       *
       * @param {Boolean} isReady - State
       */
      isReady(isReady) {
        if (isReady) {
          setTimeout(() => {
            this.highlightFeatures = false;
          }, HIGHLIGHT_FEATURE_TIME);
        }
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      this.isMounted = true;
      this.mappedUserNavigation.forEach((item) => {
        if (item.linkHighlighted && !this.isHighlightCookieSet(item.crossSystemId)) {
          const cookieName = COOKIE_NAME_MAP.highlightUserNavigationFeature.replace('{id}', item.crossSystemId);

          // eslint-disable-next-line unicorn/no-document-cookie
          document.cookie = [`${cookieName}=${Date.now()}`, 'SameSite=Lax', 'Max-Age=31536000'].join(';');
        }
      });
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Checks if a feature/link was already highlighted before (on Pimcore or SAP Commerce).
       *
       * @param {String|Number} crossSystemId - The given cross system ID.
       *
       * @returns {Boolean}
       */
      isHighlightCookieSet(crossSystemId) {
        return !!document.cookie.includes(
          COOKIE_NAME_MAP.highlightUserNavigationFeature.replace('{id}', crossSystemId),
        );
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  @keyframes c-user-navigation-link-highlighter {
    0% {
      transform: scale(1.72);
    }

    50% {
      transform: scale(1);
    }

    100% {
      transform: scale(1.72);
    }
  }

  .c-user-navigation {
    $this: &;
    $grid-area--highlight-badge: highlight-badge;
    $commerce-alignment: 1px;

    &__list {
      @extend %list-reset;

      @include mixins.media(md) {
        display: flex;
        padding: functions.rem((variables.$spacing--15 - $commerce-alignment) 0 variables.$spacing--15);
      }
    }

    &__item {
      margin-bottom: functions.rem(variables.$spacing--25);

      @include mixins.media(md) {
        margin-right: functions.rem(variables.$spacing--30);
        margin-bottom: 0;
      }

      &:last-child {
        @include mixins.media(md) {
          margin-right: 0;
        }
      }
    }

    &__link {
      @extend %button-reset;
      @extend %link--dark-blue;

      position: relative;
      display: flex;
      align-items: center;
    }

    &__link-highlight-badge {
      position: absolute;
      top: functions.rem(-5px);
      left: functions.rem(-5px);
      display: grid;
      grid-template-areas: '#{$grid-area--highlight-badge}';
      grid-template-columns: 1fr;
      grid-template-rows: 1fr;
      width: functions.rem(20px);
      height: functions.rem(20px);
      opacity: 0;
      transition: opacity variables.$transition-duration--400 ease-in-out;

      &::before,
      &::after {
        display: block;
        content: '';
        grid-area: $grid-area--highlight-badge;
        align-self: center;
        width: functions.rem(10px);
        height: functions.rem(10px);
        border-radius: 50%;
        background-color: variables.$color-secondary--5;
        justify-self: center;
      }

      &::after {
        opacity: 0.6;
        animation: c-user-navigation-link-highlighter variables.$transition-duration--1000 ease-in-out infinite;
      }
    }

    &__icon {
      width: functions.rem(24px); // Loading placeholder
      height: functions.rem(24px); // Loading placeholder
      margin-right: functions.rem(8px);
    }

    &--highlight-features {
      #{$this}__link-highlight-badge {
        opacity: 1;
      }
    }
  }
</style>
